import DomPurify from "isomorphic-dompurify";
import { useMemo } from "preact/hooks";
import defaultStyles from "./product-range.module.scss";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { LayoutStyleTypes, LayoutTypes, useLayoutType } from "@/util-react/layout";
import { FixBreadcrumb, useBreadcrumbs } from "@/elements-react/fix-breadcrumb";
import { Url } from "@/helpers/url";
import { useServices } from "@/services-context";
import { BrandCard, getList } from "@/elements-react/brands-list-images";
import { BrandGroupsProviderInterface, StaticPagePropsProviderInterface, StaticPagesType, staticPagePropsToMap } from "@/context/static-pages";
import { BrandGroup, BrandGroupTranslation } from "@/generated/graphql/b2c";

type ProductRangeStaticPageType = StaticPagePropsProviderInterface & BrandGroupsProviderInterface;

type BrandGroupTranslationNameKey = Extract<keyof BrandGroupTranslation, `name${string}`>;

function getBrandGroupTranslationName(translation: BrandGroupTranslation | null | undefined, languageCode: string): string {
  if (!translation) {
    return "";
  }
  const suffix = languageCode.charAt(0).toUpperCase() + languageCode.slice(1).toLowerCase();
  const key = `name${suffix}` as BrandGroupTranslationNameKey;
  return translation[key] ?? translation.nameEn ?? "";
}

export function useProductRangeCommon(staticPageContent: string) {
  const { t } = useTranslation();
  const { layoutType } = useLayoutType();
  const services = useServices();
  const isMobile = layoutType === LayoutTypes.TYPE_MOBILE;
  const langCode = services.parameters?.parameters?.language ?? "en";

  const content = useMemo(() => {
    return DomPurify.sanitize(staticPageContent);
  }, [staticPageContent]);

  const productRangeStaticPageService: ProductRangeStaticPageType = services.staticPages;

  const staticPageProps = productRangeStaticPageService.getStaticPageProps(StaticPagesType.productRange);
  const productRangePageProps = useMemo(() => {
    return staticPagePropsToMap(staticPageProps);
  }, [staticPageProps]);

  const brandGroups: BrandGroup[] = productRangeStaticPageService.getBrandGroups();

  const breadcrumbs = [
    {
      label: t("snv9qp34igffq94qh.product-range", "Product range"),
      url: Url.to("product-range-page"),
    },
  ];

  useBreadcrumbs(breadcrumbs);

  const brandGroupsRender = () => {
    return brandGroups.map((brandGroup) => {
      const brands = getList(brandGroup.businessBrands);

      return (
        <div className={defaultStyles.brandListItemContainer}>
          <div className={defaultStyles.titleH2Container}>
            <div className={defaultStyles.titleH2}>{getBrandGroupTranslationName(brandGroup.brandGroupTranslation, langCode)}</div>
            <div className={defaultStyles.titleH2Line}></div>
          </div>
          <div className={defaultStyles.brandList}>
            {brands.map((brand) => (
              <BrandCard brand={brand} showLogo={brandGroup.showLogo} />
            ))}
          </div>
        </div>
      );
    });
  };

  const renderRightBlock = () => {
    const brandsCount = services.brands.brands.size;

    if (
      (!services.brands?.brands?.size || !productRangePageProps.brands_text) &&
      (!productRangePageProps.distribution_agreements_count || !productRangePageProps.distribution_agreements_text) &&
      (!productRangePageProps.total_count_products || !productRangePageProps.spare_parts_text)
    ) {
      return null;
    }

    return (
      <div className={defaultStyles.rightHilights}>
        {brandsCount && productRangePageProps.brands_text && (
          <div className={defaultStyles.hilightsItem}>
            <div className={defaultStyles.hilightsItemTitle}>{brandsCount}</div>
            <div>{productRangePageProps.brands_text}</div>
          </div>
        )}
        {productRangePageProps.distribution_agreements_count && productRangePageProps.distribution_agreements_text && (
          <>
            <div className={defaultStyles.hilightsLine}></div>
            <div className={defaultStyles.hilightsItem}>
              <div className={defaultStyles.hilightsItemTitle}>{productRangePageProps.distribution_agreements_count}</div>
              <div>{productRangePageProps.distribution_agreements_text}</div>
            </div>
          </>
        )}
        {productRangePageProps.total_count_products && productRangePageProps.spare_parts_text && (
          <>
            <div className={defaultStyles.hilightsLine}></div>
            <div className={defaultStyles.hilightsItem}>
              <div className={defaultStyles.hilightsItemTitle}>{productRangePageProps.total_count_products}</div>
              <div>{productRangePageProps.spare_parts_text}</div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderContent = () => {
    const contentClassnames = classNames({
      [defaultStyles[LayoutStyleTypes.TYPE_SM]]: isMobile,
    });

    return (
      <div className={contentClassnames}>
        <div className={"static-page-content"}>
          <div className={defaultStyles.breadcrumbsNavigation}>
            <FixBreadcrumb />
          </div>
          <div>
            <div className={"section"}>
              <div className={defaultStyles.topBlock}>
                <div dangerouslySetInnerHTML={{ __html: content }} />
                {renderRightBlock()}
              </div>
              <div className={defaultStyles.brandListContainer}>{brandGroupsRender()}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return {
    renderContent,
  };
}
